import { useMemo, useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Ban,
  Boxes,
  CalendarRange,
  Flag,
  LayoutList,
  Play,
  Plus,
  Rocket,
  Target,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';import {
  Badge,
  Button,
  EmptyState,
  PageHeader,
  SegmentedControl,
  Spinner,
  cn,
  useToast,
  type BadgeTone,
} from '../../ui';
import { useActiveRoom } from '../../features/rooms/RoomContext';
import { useSprintBoard } from '../../features/sprintboard/useSprintBoard';
import { useGoals } from '../../features/goals/useGoals';
import { usePeopleDirectory } from '../../features/teams/usePeopleDirectory';
import { BOARD_STATUSES } from '../../features/tasks/taskModel';
import { sprintStatusLabel } from '../../features/sprintboard/models/sprintboardModel';
import type { BoardStatus, Task } from '../../features/tasks/types';
import { SprintBoardCard } from './SprintBoardCard';
import { SprintBoardByPerson } from './SprintBoardByPerson';
import { buildPersonLanes } from '../../features/sprintboard/models/byPersonModel';
import { CompleteTaskModal } from './CompleteTaskModal';
import { SprintInsightsPanel } from './SprintInsightsPanel';
import { CompleteSprintModal } from './CompleteSprintModal';
import { AddToSprintModal } from './AddToSprintModal';
import { SprintReportButton } from './SprintReportButton';
import { useDailyScrum } from '../../features/sprintboard/useDailyScrum';
import { buildSprintReportData } from '../../features/sprintboard/report/sprintReportModel';

const DND_MIME = 'text/plain';

const LANE_TONE: Record<BoardStatus, BadgeTone> = {
  TODO: 'info',
  IN_PROGRESS: 'accent',
  DONE: 'success',
};

/** Compact percentage-filled progress bar. */
function ProgressBar({ pct }: { pct: number }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-surface-3">
      <div
        className="h-full rounded-full bg-accent"
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </div>
  );
}

/**
 * Sprint Board (§13–14) — the active sprint's TODO / IN PROGRESS / DONE columns.
 * Cards drag between lanes (updating `boardStatus`); dropping into DONE opens the
 * completion prompt (§22). BLOCKED is an indicator on the card, never a column.
 * A compact header (§20) shows Day X/Y, the sprint goal, progress, committed
 * effort, blockers, and the Daily Scrum / Sprint Insights actions.
 */
export function SprintBoardPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { activeRoomId } = useActiveRoom();
  const {
    sprint,
    columns,
    tasks,
    backlog,
    sprintStarted,
    subtaskProgress,
    members,
    workingDays,
    capacity,
    commonReductionDays,
    subtasksByParent,
    dayInfo,
    progress,
    insights,
    loading,
    error,
    saving,
    moveTask,
    setTaskStatus,
    addToSprint,
    createInSprint,
    removeFromSprint,
    assignOwner,
    toggleBlocked,
    markAdhoc,
    completeTask,
    startSprint,
    previewCompletion,
    completeSprint,
    narrateInsights,
    narrateCompletion,
  } = useSprintBoard();
  const { goals } = useGoals();
  const { groups: scrumGroups } = useDailyScrum();
  const { people } = usePeopleDirectory();

  const goalNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of goals) m.set(g.id, g.title);
    return m;
  }, [goals]);

  // Standup highlights for the one-page report: the newest update per engineer,
  // most-recent first, as short "engineer: what they did" lines. Reuses the
  // Daily Scrum roll-up (one write, two surfaces) — capped in the report builder.
  const standupHighlights = useMemo(() => {
    const flat = scrumGroups.flatMap((g) =>
      g.entries.map((e) => ({ engineer: g.engineer, entry: e })),
    );
    flat.sort((a, b) =>
      (b.entry.updatedAt ?? '').localeCompare(a.entry.updatedAt ?? ''),
    );
    return flat.slice(0, 8).map(({ engineer, entry }) => {
      const what = entry.completed || entry.next || entry.blocker || 'update';
      return `${engineer}: ${what}`;
    });
  }, [scrumGroups]);

  // Report payload for the one-click PDF/print summary (null until a sprint
  // resolves; the header button is only rendered when present).
  const reportData = useMemo(() => {
    if (!sprint) return null;
    return buildSprintReportData({
      sprintName: sprint.name,
      goal: sprint.goal,
      sprintStatusLabel: sprintStatusLabel(sprint.status),
      dayInfo,
      insights,
      progress,
      memberCount: members.length,
      tasks,
      goals,
      standupHighlights,
    });
  }, [sprint, dayInfo, insights, progress, members, tasks, goals, standupHighlights]);

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overLane, setOverLane] = useState<BoardStatus | null>(null);
  const [completeTaskTarget, setCompleteTaskTarget] = useState<Task | null>(null);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [completeSprintOpen, setCompleteSprintOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [view, setView] = useState<'status' | 'person'>('status');

  const personLanes = useMemo(
    () =>
      buildPersonLanes(tasks, members, workingDays, {
        oncallDaysByAlias: sprint?.oncallDaysByAlias,
        commonReductionDays,
        subtasksByParent,
      }),
    [tasks, members, workingDays, sprint, commonReductionDays, subtasksByParent],
  );

  /** Reports a failed board write as a toast (shared by the inline actions). */
  const reportError = (title: string) => (err: unknown) =>
    toast({
      tone: 'error',
      title,
      description: err instanceof Error ? err.message : 'Unknown error',
    });

  /**
   * Inline (drag-free) status change. A DONE selection routes through the same
   * completion prompt as a drop into the DONE lane; other lanes persist directly.
   */
  const handleSetStatus = (task: Task, lane: BoardStatus) => {
    if ((task.boardStatus ?? 'TODO') === lane) return;
    if (lane === 'DONE') {
      setCompleteTaskTarget(task);
      return;
    }
    void setTaskStatus(task, lane).catch(reportError('Couldn’t change status'));
  };

  const handleToggleBlocked = (task: Task) =>
    void toggleBlocked(task).catch(reportError('Couldn’t update blocked state'));

  const handleRemoveFromSprint = (task: Task) =>
    void removeFromSprint(task).catch(reportError('Couldn’t remove from sprint'));

  const handleToggleAdhoc = (task: Task) =>
    void markAdhoc(task, !task.adhoc).catch(reportError('Couldn’t update scope flag'));

  const handleAssignOwner = (task: Task, owner: string) =>
    void assignOwner(task, owner).catch(reportError('Couldn’t assign owner'));

  const onDragStart = (e: DragEvent<HTMLElement>, task: Task) => {
    setDraggingId(task.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(DND_MIME, task.id);
  };
  const onDragEnd = () => {
    setDraggingId(null);
    setOverLane(null);
  };

  const findTask = (id: string): Task | undefined =>
    (['TODO', 'IN_PROGRESS', 'DONE'] as BoardStatus[])
      .flatMap((lane) => columns[lane])
      .find((t) => t.id === id);

  const handleDrop = (lane: BoardStatus) => (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    const id = e.dataTransfer.getData(DND_MIME) || draggingId;
    setOverLane(null);
    setDraggingId(null);
    if (!id) return;
    const task = findTask(id);
    if (!task || (task.boardStatus ?? 'TODO') === lane) return;
    if (lane === 'DONE') {
      // Moving to DONE prompts for actual effort (§22) before it lands.
      setCompleteTaskTarget(task);
      return;
    }
    void moveTask(task, lane).catch((err) =>
      toast({
        tone: 'error',
        title: 'Move failed',
        description: err instanceof Error ? err.message : 'Unknown error',
      }),
    );
  };

  const openTask = (task: Task) => navigate(`/tasks/${task.id}`);

  // --- No active room ------------------------------------------------------
  if (!activeRoomId) {
    return (
      <div>
        <PageHeader
          icon={<LayoutList className="h-5 w-5" />}
          title="Current Sprint"
          description="The active sprint's board."
        />
        <EmptyState
          icon={<Boxes className="h-6 w-6" />}
          title="No active room"
          description="Sprints live inside a room. Select or create a room to see its board."
          action={<Button onClick={() => navigate('/rooms')}>Go to Rooms</Button>}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading sprint board" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader icon={<LayoutList className="h-5 w-5" />} title="Current Sprint" />
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load the board"
          description={error}
        />
      </div>
    );
  }

  // --- No sprint yet -------------------------------------------------------
  if (!sprint) {
    return (
      <div>
        <PageHeader
          icon={<LayoutList className="h-5 w-5" />}
          title="Current Sprint"
          description="The active sprint's board."
        />
        <EmptyState
          icon={<CalendarRange className="h-6 w-6" />}
          title="No sprint yet"
          description="Create a sprint and plan work into it, then start it to open the board."
          action={
            <Button
              leftIcon={<CalendarRange className="h-4 w-4" />}
              onClick={() => navigate('/work/sprints')}
            >
              Go to Sprints
            </Button>
          }
        />
      </div>
    );
  }

  const isActive = sprint.status === 'active';
  const isPlanning = sprint.status === 'planning';
  const committed = progress.committedDays;

  return (
    <div>
      {/* Compact sprint header (§20) */}
      <div className="mb-5 rounded-2xl border border-surface-border bg-surface-2 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-semibold text-zinc-50">{sprint.name}</h1>
              <Badge
                tone={isActive ? 'success' : isPlanning ? 'info' : 'neutral'}
              >
                {sprintStatusLabel(sprint.status)}
              </Badge>
              {dayInfo.total > 0 && (
                <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                  <CalendarRange className="h-3.5 w-3.5" aria-hidden="true" />
                  Day {dayInfo.day}/{dayInfo.total}
                </span>
              )}
              {members.length > 0 && (
                <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                  <Users className="h-3.5 w-3.5" aria-hidden="true" />
                  {members.length}
                </span>
              )}
            </div>
            {sprint.goal && (
              <p className="mt-1 flex items-start gap-1.5 text-sm text-zinc-400">
                <Target className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" aria-hidden="true" />
                <span className="line-clamp-1">{sprint.goal}</span>
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setAddOpen(true)}
            >
              Add task
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Users className="h-4 w-4" />}
              onClick={() => navigate('/team/daily-scrum')}
            >
              Daily Scrum
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<TrendingUp className="h-4 w-4" />}
              onClick={() => setInsightsOpen(true)}
            >
              Sprint Insights
            </Button>
            {reportData && <SprintReportButton data={reportData} />}
            {isPlanning && (
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Play className="h-4 w-4" />}
                loading={saving}
                onClick={() =>
                  void startSprint().catch((e) =>
                    toast({
                      tone: 'error',
                      title: 'Couldn’t start sprint',
                      description: e instanceof Error ? e.message : 'Unknown error',
                    }),
                  )
                }
              >
                Start Sprint
              </Button>
            )}
            {isActive && (
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Flag className="h-4 w-4" />}
                onClick={() => setCompleteSprintOpen(true)}
              >
                Complete Sprint
              </Button>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-4">
          <div className="flex-1">
            <ProgressBar pct={progress.percentComplete} />
          </div>
          <span className="text-sm font-medium tabular-nums text-zinc-300">
            {progress.percentComplete}%
          </span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
          <span>
            Effort{' '}
            <span className="tabular-nums text-zinc-300">
              {progress.completedDays}
            </span>
            /
            <span className="tabular-nums text-zinc-300">{committed}</span> days
          </span>
          <span className="inline-flex items-center gap-1">
            <Ban
              className={cn(
                'h-3.5 w-3.5',
                insights.blockedCount > 0 ? 'text-red-400' : 'text-zinc-600',
              )}
              aria-hidden="true"
            />
            {insights.blockedCount} blocker{insights.blockedCount === 1 ? '' : 's'}
          </span>
          {insights.unplannedCount > 0 && (
            <span
              className="inline-flex items-center gap-1 text-amber-300"
              title="Tasks pulled into the sprint mid-flight (unplanned scope)"
            >
              <Zap className="h-3.5 w-3.5" aria-hidden="true" />
              Unplanned scope added:{' '}
              <span className="tabular-nums">{insights.unplannedCount}</span>{' '}
              {insights.unplannedCount === 1 ? 'task' : 'tasks'}
              {insights.unplannedEffortDays > 0 && (
                <>
                  {' / '}
                  <span className="tabular-nums">
                    {insights.unplannedEffortDays}
                  </span>{' '}
                  days
                </>
              )}
            </span>
          )}
        </div>

        {isPlanning && (
          <p className="mt-3 flex items-center gap-1.5 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-xs text-sky-200">
            <Rocket className="h-3.5 w-3.5" aria-hidden="true" />
            This sprint hasn't started. Plan work from the{' '}
            <button
              type="button"
              className="underline hover:text-white"
              onClick={() => navigate(`/work/sprints/${sprint.id}/plan`)}
            >
              planning board
            </button>
            , then Start Sprint.
          </p>
        )}
      </div>

      {/* View toggle: Status board (default) vs By Person */}
      <SegmentedControl
        className="mb-4"
        aria-label="Board view"
        value={view}
        onChange={(id) => setView(id as 'status' | 'person')}
        items={[
          {
            id: 'status',
            label: 'Status',
            icon: <LayoutList className="h-4 w-4" aria-hidden="true" />,
          },
          {
            id: 'person',
            label: 'By Person',
            icon: <Users className="h-4 w-4" aria-hidden="true" />,
          },
        ]}
      />

      {view === 'person' ? (
        personLanes.length === 0 ? (
          <EmptyState
            icon={<Users className="h-6 w-6" />}
            title="No assigned work yet"
            description="Once tasks have owners, they'll group here by person with capacity and counts."
          />
        ) : (
          <SprintBoardByPerson
            lanes={personLanes}
            subtaskProgress={subtaskProgress}
            goalNames={goalNames}
            onOpen={openTask}
            onSetStatus={handleSetStatus}
            onToggleBlocked={handleToggleBlocked}
            onRemoveFromSprint={handleRemoveFromSprint}
            onToggleAdhoc={handleToggleAdhoc}
            onAssignOwner={handleAssignOwner}
            people={people}
            busy={saving}
          />
        )
      ) : (
        /* Board columns */
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {BOARD_STATUSES.map((lane) => {
            const items = columns[lane.value];
            const isOver = overLane === lane.value;
            return (
              <section
                key={lane.value}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (overLane !== lane.value) setOverLane(lane.value);
                }}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setOverLane((l) => (l === lane.value ? null : l));
                  }
                }}
                onDrop={handleDrop(lane.value)}
                className={cn(
                  'flex min-h-[16rem] flex-col rounded-2xl border bg-surface-2/50 p-3 transition-colors',
                  isOver ? 'border-accent bg-surface-3/40' : 'border-surface-border',
                )}
              >
                <header className="mb-2 flex items-center gap-2 px-1">
                  <Badge tone={LANE_TONE[lane.value]}>{lane.label}</Badge>
                  <span className="rounded-full bg-surface-3 px-1.5 text-xs text-zinc-500">
                    {items.length}
                  </span>
                </header>
                <div className="flex flex-1 flex-col gap-2">
                  {items.length === 0 ? (
                    <p className="rounded-xl border border-dashed border-surface-border px-3 py-10 text-center text-xs text-zinc-600">
                      {lane.value === 'DONE'
                        ? 'Drop a task here to complete it.'
                        : 'Drop tasks here.'}
                    </p>
                  ) : (
                    items.map((t) => (
                      <SprintBoardCard
                        key={t.id}
                        task={t}
                        goalName={t.goalId ? goalNames.get(t.goalId) : undefined}
                        subtaskProgress={subtaskProgress.get(t.id)}
                        dragging={draggingId === t.id}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        onOpen={openTask}
                        onSetStatus={handleSetStatus}
                        onToggleBlocked={handleToggleBlocked}
                        onRemoveFromSprint={handleRemoveFromSprint}
                        onToggleAdhoc={handleToggleAdhoc}
                        onAssignOwner={handleAssignOwner}
                        people={people}
                        busy={saving}
                      />
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <CompleteTaskModal
        open={!!completeTaskTarget}
        onClose={() => setCompleteTaskTarget(null)}
        task={completeTaskTarget}
        onComplete={completeTask}
      />

      <SprintInsightsPanel
        open={insightsOpen}
        onClose={() => setInsightsOpen(false)}
        sprintName={sprint.name}
        insights={insights}
        onNarrate={narrateInsights}
      />

      <CompleteSprintModal
        open={completeSprintOpen}
        onClose={() => setCompleteSprintOpen(false)}
        sprintName={sprint.name}
        onPreview={previewCompletion}
        onConfirm={completeSprint}
        onNarrate={narrateCompletion}
        onCompleted={() => setCompleteSprintOpen(false)}
      />

      <AddToSprintModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        sprintName={sprint.name}
        backlog={backlog}
        capacity={capacity}
        subtasksByParent={subtasksByParent}
        defaultAdhoc={sprintStarted}
        busy={saving}
        onAddFromBacklog={addToSprint}
        onCreate={createInSprint}
      />
    </div>
  );
}

export default SprintBoardPage;
